import * as THREE from 'three';
import { clamp } from '@/core/rng';

/**
 * A Divine Anchor, and whatever mortals have raised over it.
 *
 * Two things are drawn here and they belong to different owners. The
 * **stone** is the god's: one dark upright that was placed by attendance and
 * that nothing in the world can take away. Everything around it is the
 * mortals' — a ring of offerings, a cairn, a roof, a shrine — earned only by
 * people walking back, and every rung of it is theirs to have built.
 *
 * So the stone never changes with the stage, and the stage never touches the
 * stone. If the shrine were thrown down tomorrow the stone would stand in an
 * empty field exactly as it does on the first day, which is the design's
 * "destroying the object must not erase the place's significance" made
 * literal in geometry.
 *
 * Render-only. It reads the simulation and owns none of it.
 */

export type SacredStageName =
  'bare' | 'footpath' | 'offerings' | 'cairn' | 'shelter' | 'shrine' | 'temple';

export interface AnchorView {
  id: string;
  x: number; y: number; z: number;
  stage: SacredStageName;
  /** 0..1 deity↔place. Drawn as how surely the stone stands, not as glow. */
  resonance: number;
}

/** Diorama scale, matching structures.ts and the figures. */
const MODEL = 2.0;
const CAP = 24;                 // anchors on screen at once
const OFFERING_CAP = CAP * 10;
const RUNG: Record<SacredStageName, number> =
  { bare: 0, footpath: 1, offerings: 2, cairn: 3, shelter: 4, shrine: 5, temple: 6 };

export interface Anchors {
  group: THREE.Group;
  update(tSec: number, views: AnchorView[], camR: number): void;
}

export function createAnchors(): Anchors {
  const group = new THREE.Group();
  const matte = new THREE.MeshStandardMaterial({ roughness: 0.95, metalness: 0 });

  // The stone. Tapered, tall, and darker than anything else on the hillside
  // — a thing that is obviously not a shelter and obviously not a rock.
  const stones = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0.16 * MODEL, 0.3 * MODEL, 2.6 * MODEL, 5)
      .translate(0, 1.3 * MODEL, 0),
    matte, CAP);
  stones.name = 'anchorStones';
  stones.frustumCulled = false;
  stones.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3);

  // What mortals leave: small pale stones set around it, one ring per rung.
  const offerings = new THREE.InstancedMesh(
    new THREE.DodecahedronGeometry(0.22 * MODEL, 0), matte, OFFERING_CAP);
  offerings.name = 'offerings';
  offerings.frustumCulled = false;
  offerings.instanceColor =
    new THREE.InstancedBufferAttribute(new Float32Array(OFFERING_CAP * 3), 3);

  // The cairn a long-tended place accumulates: a heap that grows with the rung.
  const cairns = new THREE.InstancedMesh(
    new THREE.ConeGeometry(0.62 * MODEL, 0.9 * MODEL, 7).translate(0, 0.45 * MODEL, 0),
    matte, CAP);
  cairns.name = 'cairns';
  cairns.frustumCulled = false;
  cairns.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3);

  // And the roof a shrine or temple finally puts over it.
  const roofs = new THREE.InstancedMesh(
    new THREE.ConeGeometry(1.5 * MODEL, 1.5 * MODEL, 4).translate(0, 2.6 * MODEL, 0)
      .rotateY(Math.PI / 4),
    matte, CAP);
  roofs.name = 'roofs';
  roofs.frustumCulled = false;
  roofs.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3);

  group.add(stones, offerings, cairns, roofs);

  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler();
  const pos = new THREE.Vector3(), scl = new THREE.Vector3();
  const col = new THREE.Color();

  /** A stable 0..1 per anchor and channel — the same place laid out forever. */
  const roll = (id: string, k: number): number => {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
    h = Math.imul(h ^ (k + 0x9e37), 0x85ebca6b);
    h ^= h >>> 13;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };

  return {
    group,
    update(tSec, views, camR) {
      // a stone this size is a landmark: it survives much further out than a
      // shelter does, because standing out is the entire point of it
      const visible = camR < 9000 && views.length > 0;
      group.visible = visible;
      if (!visible) {
        stones.count = offerings.count = cairns.count = roofs.count = 0;
        return;
      }
      let n = 0, o = 0, c = 0, r = 0;
      for (const v of views) {
        if (n >= CAP) break;
        const rung = RUNG[v.stage] ?? 0;

        // the stone: upright, faintly leaning by its own hash, never by stage
        const lean = (roll(v.id, 1) - 0.5) * 0.06;
        e.set(lean, roll(v.id, 2) * Math.PI * 2, (roll(v.id, 3) - 0.5) * 0.06, 'YXZ');
        q.setFromEuler(e);
        pos.set(v.x, v.y, v.z);
        // resonance is the god's own hold on the place: a faded one has sunk
        // a little into the ground, but it is still standing
        const h = 0.75 + 0.25 * clamp(v.resonance, 0, 1);
        scl.set(1, h, 1);
        m.compose(pos, q, scl);
        stones.setMatrixAt(n, m);
        stones.setColorAt(n, col.setRGB(0.20, 0.19, 0.21));
        n++;

        // offerings: one ring per rung above 'footpath', laid by hand
        const rings = Math.max(0, rung - 1);
        for (let k = 0; k < rings && o < OFFERING_CAP; k++) {
          const count = 5 + k * 3;
          for (let i = 0; i < count && o < OFFERING_CAP; i++, o++) {
            const a = (i / count) * Math.PI * 2 + roll(v.id, 20 + k) * 6.3;
            const rad = (1.9 + k * 1.15) * MODEL;
            pos.set(v.x + Math.cos(a) * rad, v.y, v.z + Math.sin(a) * rad);
            e.set(0, roll(v.id, 40 + i + k * 7) * 6.3, 0, 'YXZ');
            const s = 0.7 + roll(v.id, 60 + i + k * 11) * 0.7;
            m.compose(pos, q.setFromEuler(e), scl.set(s, s * 0.75, s));
            offerings.setMatrixAt(o, m);
            offerings.setColorAt(o, col.setRGB(0.62, 0.59, 0.52));
            o++;
          }
        }

        // a cairn from 'cairn' up, growing with every rung after it
        if (rung >= 3 && c < CAP) {
          const g = 1.0 + (rung - 3) * 0.3;
          pos.set(v.x + 1.1 * MODEL, v.y, v.z + 0.7 * MODEL);
          e.set(0, roll(v.id, 5) * 6.3, 0, 'YXZ');
          m.compose(pos, q.setFromEuler(e), scl.set(g, g, g));
          cairns.setMatrixAt(c, m);
          cairns.setColorAt(c, col.setRGB(0.50, 0.48, 0.44));
          c++;
        }

        // and a roof from the rung the Chronicle calls "they roofed the
        // place" — shelter — growing into a shrine and then a temple
        if (rung >= 4 && r < CAP) {
          const g = 0.8 + (rung - 4) * 0.3;
          pos.set(v.x, v.y, v.z);
          e.set(0, roll(v.id, 7) * 6.3, 0, 'YXZ');
          m.compose(pos, q.setFromEuler(e), scl.set(g, g, g));
          roofs.setMatrixAt(r, m);
          roofs.setColorAt(r, col.setRGB(0.42, 0.34, 0.24));
          r++;
        }
      }
      stones.count = n; offerings.count = o; cairns.count = c; roofs.count = r;
      for (const mesh of [stones, offerings, cairns, roofs]) {
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      }
      void tSec;
    },
  };
}
